import React from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import VerifiedIcon from 'images/verified.svg';
import BuildingIcon from 'images/icons/building.svg';
import { PhoneIcon } from '../../../ui/icons';
import { PropertyManagerFooter } from './PropertyManagerFooter';
import * as styles from './propertyManagerModule.styles';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const formatPhoneNumber = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

export const parseOfficeHours = (contactTimes: string): Array<string> => {
  const dayPattern = new RegExp(`(${DAYS.join('|')})`, 'g');
  const hasDayLabels = dayPattern.test(contactTimes);

  if (!hasDayLabels) return [contactTimes];

  const lines: Array<string> = [];
  const splitPattern = new RegExp(`(?=${DAYS.join('|')})`, 'g');
  const parts = contactTimes.split(splitPattern).filter(Boolean);

  for (const part of parts) {
    lines.push(part.trim());
  }

  return lines;
};

export const PropertyManagerModule = () => {
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  if (!listing) return null;

  const { trusted, details, displayName, address, listedBy } = listing;
  const contactTimes = details?.contactTimes;
  const officeHoursLines = contactTimes ? parseOfficeHours(contactTimes) : [];
  const managerName = displayName && displayName !== address?.street ? displayName : '';
  const companyName = listedBy?.companyName;
  const contactPhone = listedBy?.contactPhone;

  if (!managerName && !trusted && officeHoursLines.length === 0) {
    return null;
  }

  return (
    <div>
      {managerName && <div className={styles.managerName}>{managerName}</div>}
      {companyName && <div className={styles.companyName}>{companyName}</div>}
      {trusted && (
        <div className={styles.verifiedBadge}>
          <img src={VerifiedIcon} alt="" aria-hidden="true" className={styles.verifiedIcon} />
          <span>Verified listing</span>
        </div>
      )}
      {contactPhone && (
        <div className={styles.desktopContactSection}>
          <div className={styles.desktopContactLabel}>
            <span className={styles.desktopContactIcon} aria-hidden="true">
              <PhoneIcon />
            </span>
            <span>Contact</span>
          </div>
          <a href={`tel:${contactPhone.replace(/\D/g, '')}`} className={styles.desktopContactPhone}>
            {formatPhoneNumber(contactPhone)}
          </a>
        </div>
      )}
      {officeHoursLines.length > 0 && (
        <div className={styles.officeHours}>
          <div className={styles.officeHoursLabel}>
            <img src={BuildingIcon} alt="" aria-hidden="true" className={styles.managerIcon} />
            <span>Office hours</span>
          </div>
          {officeHoursLines.map((line) => (
            <div key={line} className={styles.officeHoursLine}>
              {line}
            </div>
          ))}
        </div>
      )}
      <PropertyManagerFooter />
    </div>
  );
};
